import * as THREE from 'three';
import { TOWER_POSITION } from '@shared/constants';

// ============================================================
// Radar sweep minimap — rotating line reveals entities
// ============================================================

const MAP_RADIUS_WORLD = 450;
const SWEEP_SPEED = 3;         // radians per second
const BLIP_LIFETIME = 2.5;    // seconds a blip stays visible after sweep
const SWEEP_WIDTH = 0.15;     // radians — how wide the sweep beam is

interface MinimapEntity {
  readonly object: THREE.Object3D;
  readonly color: string;
  readonly minRadarAltitude: number;
}

interface Blip {
  x: number;
  y: number;
  color: string;
  age: number;
}

const _dir = new THREE.Vector2();
const _worldDir = new THREE.Vector3();

export class Minimap {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly size: number;
  private readonly center: number;
  private readonly entities: MinimapEntity[] = [];
  private readonly blips: Blip[] = [];
  private readonly dpr: number;
  private sweepAngle = 0;

  constructor() {
    this.canvas = document.getElementById('minimap-canvas') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d')!;
    this.dpr = 1; // keep 1:1 to avoid double-circle from DPR mismatch
    this.canvas.width = 150;
    this.canvas.height = 150;
    this.size = 150;
    this.center = this.size / 2;
  }

  addEntity(object: THREE.Object3D, color: string, minRadarAltitude = 0): void {
    this.entities.push({ object, color, minRadarAltitude });
  }

  removeEntity(object: THREE.Object3D): void {
    const idx = this.entities.findIndex((e) => e.object === object);
    if (idx !== -1) this.entities.splice(idx, 1);
  }

  private readonly instantBlips: { pos: THREE.Vector3; color: string }[] = [];

  /** Add a one-frame blip (call every frame for missiles, etc.) */
  addInstantBlip(pos: THREE.Vector3, color: string): void {
    this.instantBlips.push({ pos, color });
  }

  update(player: THREE.Object3D, dt = 0.016): void {
    const ctx = this.ctx;
    const s = this.size;
    const c = this.center;
    const r = c - 2;
    const scale = r / MAP_RADIUS_WORLD;

    // Advance sweep
    const prevAngle = this.sweepAngle;
    this.sweepAngle = (this.sweepAngle + SWEEP_SPEED * dt) % (Math.PI * 2);

    ctx.clearRect(0, 0, s * this.dpr, s * this.dpr);

    // Clip to circle
    ctx.save();
    ctx.beginPath();
    ctx.arc(c, c, r, 0, Math.PI * 2);
    ctx.clip();

    // Background
    ctx.fillStyle = 'rgba(0, 20, 0, 0.7)';
    ctx.fillRect(0, 0, s, s);

    // Concentric range rings
    ctx.strokeStyle = 'rgba(0, 255, 0, 0.1)';
    ctx.lineWidth = 0.5;
    for (let i = 1; i <= 3; i++) {
      ctx.beginPath();
      ctx.arc(c, c, (r / 3) * i, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Cross lines
    ctx.beginPath();
    ctx.moveTo(c, 2);
    ctx.lineTo(c, s - 2);
    ctx.moveTo(2, c);
    ctx.lineTo(s - 2, c);
    ctx.stroke();

    // Player direction
    player.getWorldDirection(_worldDir);
    const playerAngle = Math.atan2(-_worldDir.x, -_worldDir.z);

    // Sweep line
    const sweepX = c + Math.cos(this.sweepAngle) * r;
    const sweepY = c + Math.sin(this.sweepAngle) * r;

    // Sweep trail (fading gradient)
    const grad = ctx.createConicalGradient
      ? null // not supported everywhere
      : null;

    // Draw sweep trail manually with arcs
    for (let i = 0; i < 20; i++) {
      const trailAngle = this.sweepAngle - (i / 20) * 0.8;
      const alpha = 0.3 * (1 - i / 20);
      ctx.strokeStyle = `rgba(0, 255, 0, ${alpha})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(c, c);
      ctx.lineTo(
        c + Math.cos(trailAngle) * r,
        c + Math.sin(trailAngle) * r,
      );
      ctx.stroke();
    }

    // Bright sweep line
    ctx.strokeStyle = 'rgba(0, 255, 0, 0.8)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(c, c);
    ctx.lineTo(sweepX, sweepY);
    ctx.stroke();

    // Check entities against sweep
    for (const entity of this.entities) {
      if (entity.minRadarAltitude > 0 && entity.object.position.y < entity.minRadarAltitude) continue;
      if (!entity.object.visible) continue;

      const dx = entity.object.position.x - player.position.x;
      const dz = entity.object.position.z - player.position.z;
      const rot = this.rotatePoint(dx, dz, playerAngle);
      const sx = c + rot.x * scale;
      const sy = c + rot.y * scale;

      if (!this.isInCircle(sx, sy)) continue;

      // Check if sweep just passed over this entity
      const entityAngle = Math.atan2(sy - c, sx - c);
      const angleDiff = this.normalizeAngle(entityAngle - this.sweepAngle);
      if (Math.abs(angleDiff) < SWEEP_WIDTH) {
        // Refresh blip
        this.addOrRefreshBlip(sx, sy, entity.color);
      }
    }

    // Ship marker — always visible
    const shipDx = TOWER_POSITION.x - player.position.x;
    const shipDz = TOWER_POSITION.z - player.position.z;
    const shipRot = this.rotatePoint(shipDx, shipDz, playerAngle);
    const shipSx = c + shipRot.x * scale;
    const shipSy = c + shipRot.y * scale;
    if (this.isInCircle(shipSx, shipSy)) {
      ctx.fillStyle = 'rgba(0, 255, 0, 0.6)';
      ctx.fillRect(shipSx - 3, shipSy - 1, 6, 2);
    }

    // Draw blips
    for (let i = this.blips.length - 1; i >= 0; i--) {
      const b = this.blips[i];
      b.age += dt;
      if (b.age >= BLIP_LIFETIME) {
        this.blips.splice(i, 1);
        continue;
      }
      const alpha = 1 - b.age / BLIP_LIFETIME;
      ctx.fillStyle = b.color.replace(')', `, ${alpha})`).replace('rgb', 'rgba');
      // Fallback for hex colors
      if (b.color.startsWith('#')) {
        ctx.globalAlpha = alpha;
        ctx.fillStyle = b.color;
      }
      ctx.beginPath();
      ctx.arc(b.x, b.y, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Instant blips (missiles, etc.) — always visible, no sweep needed
    for (const blip of this.instantBlips) {
      const dx = blip.pos.x - player.position.x;
      const dz = blip.pos.z - player.position.z;
      const rot = this.rotatePoint(dx, dz, playerAngle);
      const sx = c + rot.x * scale;
      const sy = c + rot.y * scale;
      if (this.isInCircle(sx, sy)) {
        ctx.fillStyle = blip.color;
        ctx.beginPath();
        ctx.arc(sx, sy, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    this.instantBlips.length = 0;

    // Player dot (center)
    ctx.fillStyle = '#0f0';
    ctx.beginPath();
    ctx.arc(c, c, 3, 0, Math.PI * 2);
    ctx.fill();

    // Border
    ctx.restore();
    ctx.strokeStyle = 'rgba(0, 255, 0, 0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(c, c, r, 0, Math.PI * 2);
    ctx.stroke();
  }

  private addOrRefreshBlip(x: number, y: number, color: string): void {
    // Update existing nearby blip or add new
    for (const b of this.blips) {
      const dx = b.x - x;
      const dy = b.y - y;
      if (dx * dx + dy * dy < 25) {
        b.x = x;
        b.y = y;
        b.age = 0;
        return;
      }
    }
    this.blips.push({ x, y, color, age: 0 });
  }

  private normalizeAngle(a: number): number {
    while (a > Math.PI) a -= Math.PI * 2;
    while (a < -Math.PI) a += Math.PI * 2;
    return a;
  }

  private rotatePoint(dx: number, dz: number, angle: number): { x: number; y: number } {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
      x: -(dx * cos - dz * sin),
      y: -(dx * sin + dz * cos),
    };
  }

  private isInCircle(sx: number, sy: number): boolean {
    const dx = sx - this.center;
    const dy = sy - this.center;
    return dx * dx + dy * dy < (this.center - 4) * (this.center - 4);
  }
}
